import { type ChangeEvent, type FC, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { cx } from "@emotion/css";
import { PopoverPlacementTypes } from "@snapchat/snap-design-system";
import { PencilIcon } from "@snapchat/snap-design-system-icons";
import { Input } from "antd";
import { type TextAreaRef } from "antd/lib/input/TextArea";

import { Flexbox } from "../../core/components";
import { KeyboardKey } from "../model";

import { ActionDisabledTooltip } from "./ActionDisabledTooltip";
import { EditIconButton } from "./EditIconButton";
import {
    editIconButtonCss,
    inputStyles,
    placeholderCss,
    spanCss,
    textAreaCss,
    textContainerCss,
} from "./EditableText.styles";

const { TextArea } = Input;

export interface EditableTextAreaProps {
    value: string;
    className?: string;
    isDisabled: boolean;
    placeholder?: string;
    onChange: (value: string) => void;
    hasPermission: boolean;
    maxLength?: number;
}

export const EditableTextArea: FC<EditableTextAreaProps> = ({
    value,
    className,
    isDisabled,
    placeholder,
    onChange,
    hasPermission,
    maxLength,
}) => {
    const [localValue, setLocalValue] = useState("");
    const [isEditMode, setEditMode] = useState(false);
    const textAreaRef = useRef<TextAreaRef | null>();

    const toggleEditMode = useCallback(() => {
        setEditMode((isEnabled) => !isEnabled);
    }, []);

    const applyNewValueIfValid = useCallback(() => {
        const newValue = localValue.trim();
        if (newValue === value) {
            setLocalValue(value);
        } else {
            onChange(newValue);
        }
        toggleEditMode();
    }, [localValue, value, toggleEditMode, onChange]);

    const exitEditMode = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            switch (event.key as KeyboardKey) {
                case KeyboardKey.Enter:
                    applyNewValueIfValid();
                    break;
                case KeyboardKey.Esc:
                case KeyboardKey.Escape:
                    toggleEditMode();
                    setLocalValue(value);
                    break;
            }
        },
        [applyNewValueIfValid, toggleEditMode, value],
    );

    const onValueChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        // this check avoids the cursor moving to the next line when enter key is pressed
        if (!newValue.endsWith("\n")) {
            setLocalValue(newValue);
        }
    };

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditMode && textAreaRef.current) {
            textAreaRef.current.focus();

            const refObj = textAreaRef.current.resizableTextArea?.textArea;

            const refValue = refObj?.value;
            if (refValue) {
                refObj.setSelectionRange(refValue.length, refValue.length);
            }
        }
    }, [isEditMode]);

    const formatter = useCallback(({ count, maxLength }: { count: number; maxLength?: number }) => {
        return `${maxLength ? maxLength - count : count}`;
    }, []);

    return (
        <Flexbox alignItems="flex-start" className={textContainerCss}>
            {isEditMode ? (
                <TextArea
                    bordered={false}
                    autoSize
                    ref={(node) => (textAreaRef.current = node ?? null)}
                    className={cx(inputStyles, className, textAreaCss)}
                    value={localValue}
                    readOnly={!isEditMode}
                    onChange={onValueChange}
                    onKeyUp={exitEditMode}
                    onBlur={applyNewValueIfValid}
                    maxLength={maxLength}
                    showCount={{ formatter }}
                />
            ) : (
                <span className={cx({ [className ?? ""]: true, [spanCss]: true, [placeholderCss]: !value })}>
                    {value || placeholder}
                </span>
            )}
            <ActionDisabledTooltip hasPermission={hasPermission} placement={PopoverPlacementTypes.TOP_LEFT}>
                <EditIconButton
                    show={!isEditMode}
                    icon={<PencilIcon />}
                    onClick={toggleEditMode}
                    isDisabled={isDisabled}
                    className={editIconButtonCss}
                />
            </ActionDisabledTooltip>
        </Flexbox>
    );
};
